import {
  DEFAULT_TRACE_MIN_DISTANCE_M,
  TRACE_DISTANCE_OPTIONS_M,
  haversineMetres,
  type TraceDistanceOptionM,
} from './fieldMappingGps';

export type TraceLatLng = { lat: number; lng: number };

/** Max distance from the dwell anchor for fixes to count as the same rest. */
export const TRACE_RESTING_CLUSTER_RADIUS_M = 8;

/** Minimum consecutive fixes before we treat a run as resting (not slow walk). */
export const TRACE_RESTING_MIN_CLUSTER_SIZE = 3;

/** Drop fixes that spike far off the local chord. */
export const TRACE_OUTLIER_SPIKE_M = 15;

/** Drop mid-path vertices that sit almost on the chord between neighbours (straighten). */
export const TRACE_STRAIGHTEN_COLINEAR_M = 2;

/** Default resample spacing when optional resampling is enabled on stop. */
export const TRACE_RESAMPLE_SPACING_M = DEFAULT_TRACE_MIN_DISTANCE_M;

export type TracePostProcessOptions = {
  /** Evenly space vertices along the path after resting/outlier cleanup. */
  resample?: boolean;
  resampleSpacingM?: TraceDistanceOptionM;
};

function samePoint(a: TraceLatLng, b: TraceLatLng): boolean {
  return a.lat === b.lat && a.lng === b.lng;
}

function pushPoint(out: TraceLatLng[], point: TraceLatLng): void {
  if (out.length === 0 || !samePoint(out[out.length - 1], point)) {
    out.push(point);
  }
}

function lerp(a: TraceLatLng, b: TraceLatLng, t: number): TraceLatLng {
  return {
    lat: a.lat + t * (b.lat - a.lat),
    lng: a.lng + t * (b.lng - a.lng),
  };
}

/**
 * Scan the full trace with a running resting radius from each segment anchor.
 * While fixes stay within `radiusM` of the anchor (first fix of the run), they
 * belong to the same dwell. Intermediates are dropped; **first and last** of
 * each qualifying dwell are kept so walking legs before/after are not shortened.
 */
export function collapseRestingClusters(
  points: TraceLatLng[],
  radiusM = TRACE_RESTING_CLUSTER_RADIUS_M,
  minRunLength = TRACE_RESTING_MIN_CLUSTER_SIZE,
): TraceLatLng[] {
  if (points.length <= 2) return [...points];

  const out: TraceLatLng[] = [];
  let i = 0;
  const lastIndex = points.length - 1;

  while (i <= lastIndex) {
    if (i === lastIndex) {
      pushPoint(out, points[lastIndex]);
      break;
    }

    const anchor = points[i];
    let j = i + 1;

    while (
      j <= lastIndex
      && haversineMetres(anchor.lat, anchor.lng, points[j].lat, points[j].lng) <= radiusM
    ) {
      j += 1;
    }

    const runLength = j - i;

    if (runLength >= minRunLength) {
      pushPoint(out, points[i]);
      const runLast = points[j - 1];
      if (j - 1 > i) {
        pushPoint(out, runLast);
      }
      i = j;
    } else {
      pushPoint(out, points[i]);
      i += 1;
    }
  }

  return out;
}

function perpendicularDistanceM(
  point: TraceLatLng,
  lineStart: TraceLatLng,
  lineEnd: TraceLatLng,
): number {
  const lineLen = haversineMetres(lineStart.lat, lineStart.lng, lineEnd.lat, lineEnd.lng);
  if (lineLen === 0) {
    return haversineMetres(point.lat, point.lng, lineStart.lat, lineStart.lng);
  }

  const toRad = Math.PI / 180;
  const latMid = ((lineStart.lat + lineEnd.lat) / 2) * toRad;
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos(latMid);

  const x0 = point.lng * mPerDegLng;
  const y0 = point.lat * mPerDegLat;
  const x1 = lineStart.lng * mPerDegLng;
  const y1 = lineStart.lat * mPerDegLat;
  const x2 = lineEnd.lng * mPerDegLng;
  const y2 = lineEnd.lat * mPerDegLat;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const t = Math.max(0, Math.min(1, ((x0 - x1) * dx + (y0 - y1) * dy) / (dx * dx + dy * dy)));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  return Math.hypot(x0 - projX, y0 - projY);
}

/** Remove single-fix GPS spikes off the local chord. */
export function removeTraceOutliers(
  points: TraceLatLng[],
  spikeM = TRACE_OUTLIER_SPIKE_M,
): TraceLatLng[] {
  if (points.length <= 2) return [...points];

  const out: TraceLatLng[] = [points[0]];

  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    const offChordM = perpendicularDistanceM(curr, prev, next);
    const viaM = haversineMetres(prev.lat, prev.lng, curr.lat, curr.lng)
      + haversineMetres(curr.lat, curr.lng, next.lat, next.lng);
    const chordM = haversineMetres(prev.lat, prev.lng, next.lat, next.lng);

    const isSpike = offChordM >= spikeM && viaM - chordM >= spikeM;
    if (!isSpike) {
      out.push(curr);
    }
  }

  pushPoint(out, points[points.length - 1]);
  return out;
}

/** Remove mid-path vertices that barely deviate from a straight segment. */
export function straightenTracePath(
  points: TraceLatLng[],
  colinearM = TRACE_STRAIGHTEN_COLINEAR_M,
): TraceLatLng[] {
  if (points.length <= 2) return [...points];

  const out: TraceLatLng[] = [points[0]];
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = out[out.length - 1];
    const curr = points[i];
    const next = points[i + 1];
    if (perpendicularDistanceM(curr, prev, next) >= colinearM) {
      pushPoint(out, curr);
    }
  }
  pushPoint(out, points[points.length - 1]);
  return out;
}

/** Place vertices at even intervals along the path (~one every N m). */
export function resampleTracePath(
  points: TraceLatLng[],
  spacingM = TRACE_RESAMPLE_SPACING_M,
): TraceLatLng[] {
  if (points.length <= 1) return [...points];
  if (spacingM <= 0) return [...points];

  const cumulativeM: number[] = [0];
  for (let i = 1; i < points.length; i += 1) {
    cumulativeM.push(
      cumulativeM[i - 1]
      + haversineMetres(
        points[i - 1].lat,
        points[i - 1].lng,
        points[i].lat,
        points[i].lng,
      ),
    );
  }

  const totalM = cumulativeM[cumulativeM.length - 1];
  if (totalM === 0) return [points[0]];

  const out: TraceLatLng[] = [points[0]];
  let targetM = spacingM;

  while (targetM < totalM) {
    let seg = 1;
    while (seg < cumulativeM.length && cumulativeM[seg] < targetM) {
      seg += 1;
    }
    if (seg >= cumulativeM.length) break;

    const segStartM = cumulativeM[seg - 1];
    const segLenM = cumulativeM[seg] - segStartM;
    const t = segLenM === 0 ? 0 : (targetM - segStartM) / segLenM;
    pushPoint(out, lerp(points[seg - 1], points[seg], t));
    targetM += spacingM;
  }

  pushPoint(out, points[points.length - 1]);
  return out;
}

/** True when any segment exceeds spacing — worth resampling after endpoint snap. */
export function tracePathNeedsResample(
  points: TraceLatLng[],
  spacingM: number,
): boolean {
  if (points.length < 2 || spacingM <= 0) return false;
  for (let i = 1; i < points.length; i += 1) {
    const d = haversineMetres(
      points[i - 1].lat,
      points[i - 1].lng,
      points[i].lat,
      points[i].lng,
    );
    if (d > spacingM * 1.05) return true;
  }
  return false;
}

/** Resample when snap created long endpoint segments (fills gaps along the path). */
export function resampleTracePathIfGap(
  points: TraceLatLng[],
  spacingM: number,
): TraceLatLng[] {
  if (!tracePathNeedsResample(points, spacingM)) return [...points];
  return resampleTracePath(points, spacingM as TraceDistanceOptionM);
}

/** Next larger spacing preset for manual thin (↓ point count). */
export function pickThinSpacingM(referenceM: TraceDistanceOptionM): TraceDistanceOptionM {
  const larger = TRACE_DISTANCE_OPTIONS_M.filter((option) => option > referenceM);
  return larger[0] ?? TRACE_DISTANCE_OPTIONS_M[TRACE_DISTANCE_OPTIONS_M.length - 1];
}

/** Next smaller spacing preset for manual resample finer (↑ point count), if any. */
export function pickFinerSpacingM(referenceM: TraceDistanceOptionM): TraceDistanceOptionM | null {
  const smaller = TRACE_DISTANCE_OPTIONS_M.filter((option) => option < referenceM);
  return smaller.length > 0 ? smaller[smaller.length - 1] : null;
}

/** Reduce vertices by resampling at a wider spacing (manual, post-stop). */
export function thinTracePath(
  points: TraceLatLng[],
  spacingM: TraceDistanceOptionM = 10,
): TraceLatLng[] {
  return resampleTracePath(points, spacingM);
}

/** Add vertices by resampling at a tighter spacing (manual, post-stop). */
export function resampleFinerTracePath(
  points: TraceLatLng[],
  spacingM: TraceDistanceOptionM,
): TraceLatLng[] {
  return resampleTracePath(points, spacingM);
}

export type TracePostProcessResult = {
  points: TraceLatLng[];
  rawCount: number;
  afterRestingCount: number;
  afterOutlierCount: number;
  finalCount: number;
  resampled: boolean;
};

/**
 * Stop-trace pipeline:
 * 1. Resting collapse — drop dwell intermediates, keep first+last per rest.
 * 2. Outlier strip — remove GPS spikes.
 * 3. Optional resample — even spacing along the cleaned path.
 */
export function postProcessTracePath(
  points: TraceLatLng[],
  options: TracePostProcessOptions = {},
): TracePostProcessResult {
  const rawCount = points.length;
  if (rawCount === 0) {
    return {
      points: [],
      rawCount: 0,
      afterRestingCount: 0,
      afterOutlierCount: 0,
      finalCount: 0,
      resampled: false,
    };
  }
  if (rawCount === 1) {
    return {
      points: [...points],
      rawCount,
      afterRestingCount: 1,
      afterOutlierCount: 1,
      finalCount: 1,
      resampled: false,
    };
  }

  const afterResting = collapseRestingClusters(points);
  const afterOutliers = removeTraceOutliers(afterResting);
  const resample = options.resample === true;
  const finalPoints = resample
    ? resampleTracePath(afterOutliers, options.resampleSpacingM ?? TRACE_RESAMPLE_SPACING_M)
    : afterOutliers;

  return {
    points: finalPoints,
    rawCount,
    afterRestingCount: afterResting.length,
    afterOutlierCount: afterOutliers.length,
    finalCount: finalPoints.length,
    resampled: resample,
  };
}
